import { useEffect, useState } from 'react';
import {
  FiClock, FiAlertTriangle, FiDollarSign, FiBook, FiSmartphone, FiActivity,
  FiImage, FiPackage, FiUsers, FiAward, FiStar, FiGift, FiZap, FiRepeat,
  FiTrendingUp, FiCreditCard, FiBell, FiMousePointer, FiEye,
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/auth';
import GA4EventReports, { GA4EventReportsData } from '../components/GA4EventReports';
import CrashDetailModal from '../components/CrashDetailModal';
import LensStatsModal from '../components/LensStatsModal';
import UserStatsModal from '../components/UserStatsModal';

interface DailyPoint { _id: string; count: number }

interface DashboardStats {
  totals: { users: number; extensions: number; generations: number; articles: number; publicExtensions: number };
  last7Days: { generations: number; extensions: number; newUsers: number; articles: number };
  last30Days: { generations: number; extensions: number; newUsers: number };
  planBreakdown: Record<string, number>;
  platformDistribution: Record<string, number>;
  appVersionDistribution: Record<string, number>;
  registeredActivity?: { dau: number; wau: number; mau: number; stickiness: number; newUsers: number; activatedNewUsers: number };
  // Generation feature usage computed from the DB (mode field), replacing the GA4 customEvent:feature split
  featureBreakdown?: { feature: string; count: number }[];
  firstGenerationBreakdown?: { feature: string; count: number }[];
  pendingWaitlist: number;
  pendingPayouts: number;
  suspendedExtensions: number;
  dailyGenerations: DailyPoint[];
  dailyNewUsers: DailyPoint[];
  dailyNewExtensions: DailyPoint[];
  // Extension-promotion engagement across campaigns from the last N days.
  extensionPromotion?: {
    campaigns: number;
    windowDays: number;
    recipients: number;
    readCount: number;
    interactedCount: number;
    usedCount: number;
    readRate: number;
    interactionRate: number;
    usageRate: number;
    usageOfInteractedRate: number;
    approx: boolean;
  };
}

interface GA4DailyMetrics {
  date: string;
  activeUsers: number;
  sessions: number;
  averageSessionDuration: number;
  screenPageViews: number;
}

interface GA4Summary {
  activeUsers: number;
  sessions: number;
  averageSessionDuration: number;
  screenPageViews: number;
}

interface GA4ActiveUsers { dau: number; wau: number; mau: number; stickiness: number }
interface GA4Retention { d1: number | null; d7: number | null; d30: number | null }
interface GA4FeatureEntry { feature: string; count: number }
interface GA4Activation { newUsers: number; activatedUsers: number; rate: number | null }
interface GA4CrashFree { crashes: number; crashingUsers: number; activeUsers: number; crashFreeUsersRate: number | null }

interface GA4Data extends GA4EventReportsData {
  daily: GA4DailyMetrics[];
  summary: GA4Summary;
  appVersions: GA4AppVersion[];
  activeUsers?: GA4ActiveUsers;
  avgEngagementPerSession?: number;
  retention?: GA4Retention;
  retentionStatus?: 'empty' | 'available' | 'unavailable';
  featureBreakdown?: GA4FeatureEntry[];
  firstGenerationBreakdown?: GA4FeatureEntry[];
  activation?: GA4Activation;
  crashFree?: GA4CrashFree;
}

interface GA4AppVersion {
  version: string;
  activeUsers: number;
}

// --- public stats (leaderboards & referral) ---
interface TopExtension {
  extensionId: string; generationCount: number; name: string;
  thumbnailImageUrl: string | null; creatorId: string; creatorName: string; creatorAvatarUrl: string | null;
}
interface TopCreator { creatorId: string; generationCount: number; name: string; avatarUrl: string | null }
interface TopReferrer { referrerId: string; referralCount: number; name: string; avatarUrl: string | null }
interface ReferralConversion {
  totalReferred: number; paidReferred: number; conversionRate: number; kFactor?: number;
  byPlan: { free: number; basic: number; pro: number; premium: number };
}
interface PublicStats {
  topExtensions: { allTime: TopExtension[]; last7Days: TopExtension[]; trending: TopExtension[] };
  topCreators: { allTime: TopCreator[]; last7Days: TopCreator[]; trending: TopCreator[] };
  topReferrers: TopReferrer[];
  referralConversion: ReferralConversion;
  walkInUsers: number; // non-internal users who registered without a referral code
  exhaustedNoTopupUsers: number; // non-internal users with too few credits to generate (<8) who never bought a top-up
  usersByPlan: { free: number; basic: number; pro: number; premium: number };
}

// --- funnels & behaviour (from /api/admin/funnel-stats) ---
// All counts are distinct users per step within the window (not a strict cohort),
// so a later step can exceed an earlier one. Onboarding/paywall/permission steps
// come from the app's AnalyticsEvent store (zero until the instrumented app ships);
// firstFraw/checkout/purchase are from existing data.
interface TierStep { total: number; basic: number; pro: number; premium: number }
interface ActivationFunnel { startOnboarding: number; finishOnboarding: number; firstFraw: number }
interface MoneyFunnel {
  firstFraw: number;
  paywallViews: TierStep;
  paywallTaps: TierStep;
  startCheckout: TierStep;
  successPurchase: TierStep;
}
interface BehaviourCards {
  paywallViews: number;
  paywallTaps: number;
  notifications: { enabled: number; total: number; granted: number; denied: number };
}
interface FunnelWindow { activationFunnel: ActivationFunnel; moneyFunnel: MoneyFunnel; cards: BehaviourCards }
interface FunnelStats { all: FunnelWindow; last30Days: FunnelWindow; last7Days: FunnelWindow }

// --- purchase trends (from /api/admin/purchase-stats) ---
// Always a full 30-day zero-filled window; the 7-day view is the tail of it, so
// the toggle is a client-side slice rather than a second request.
interface SubscriptionTrendPoint { date: string; basic: number; pro: number; premium: number }
interface TopUpTrendPoint { date: string; lite: number; pro: number; other: number }
interface PurchaseStats {
  windowDays: number;
  subscriptions: SubscriptionTrendPoint[];
  topUps: TopUpTrendPoint[];
}

// --- theme tokens ---
const BG_CARD = '#1c2128';
const TEXT = '#e6edf3';
const TEXT_DIM = '#8b949e';
const BORDER = '#30363d';
const BLUE = '#58a6ff';
const PURPLE = '#a371f7';
const GREEN = '#3fb950';
const YELLOW = '#d29922';
const ORANGE = '#f0883e';
const RED = '#f85149';

// --- helper: fill missing dates in a daily series ---
function fillDailySeries(raw: DailyPoint[], days: number): { date: string; count: number }[] {
  const map = new Map(raw.map((d) => [d._id, d.count]));
  const result: { date: string; count: number }[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    result.push({ date: key, count: map.get(key) || 0 });
  }
  return result;
}

function formatDateLabel(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatPct(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined) return '—';
  return `${(ratio * 100).toFixed(1)}%`;
}

// --- stat card ---
const cardStyle: React.CSSProperties = {
  background: BG_CARD,
  borderRadius: 12,
  padding: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  border: `1px solid ${BORDER}`,
};

/**
 * A metric tile. Pass `onPress` to make it open a detail view: the tile becomes a
 * real button, so it is keyboard-reachable and focusable, and lights its border
 * on hover. Deliberately no caption — the tile keeps the same layout as a plain
 * one, so a grid of cards reads as one row of numbers rather than a row of
 * call-to-actions.
 */
function StatCard({ icon, label, value, sub, color, onPress }: {
  icon: React.ReactNode; label: string; value: number | string; sub?: string; color: string;
  onPress?: () => void;
}) {
  const [hover, setHover] = useState(false);

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color, fontSize: 20,
        }}>{icon}</div>
        <span style={{ color: TEXT_DIM, fontSize: 13, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ color: TEXT, fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div style={{ color: TEXT_DIM, fontSize: 12 }}>{sub}</div>}
    </>
  );

  if (!onPress) return <div style={cardStyle}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        borderColor: hover ? color : BORDER,
        transition: 'border-color 0.15s',
      }}
    >
      {body}
    </button>
  );
}

// --- custom tooltip ---
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8,
      padding: '8px 12px', fontSize: 12, color: TEXT,
    }}>
      <div style={{ color: TEXT_DIM, marginBottom: 4 }}>{formatDateLabel(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
}

/**
 * Tooltip for the stacked purchase bars: every segment plus the day's total,
 * which is the number the stack's height actually represents and which no single
 * payload entry carries.
 */
function StackedTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
  return (
    <div style={{
      background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8,
      padding: '8px 12px', fontSize: 12, color: TEXT,
    }}>
      <div style={{ color: TEXT_DIM, marginBottom: 4 }}>{formatDateLabel(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {(p.value || 0).toLocaleString()}
        </div>
      ))}
      <div style={{ color: TEXT, fontWeight: 600, marginTop: 4, borderTop: `1px solid ${BORDER}`, paddingTop: 4 }}>
        Total: {total.toLocaleString()}
      </div>
    </div>
  );
}

function GA4Tooltip({ active, payload, label, formatter }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8,
      padding: '8px 12px', fontSize: 12, color: TEXT,
    }}>
      <div style={{ color: TEXT_DIM, marginBottom: 4 }}>{formatDateLabel(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {formatter ? formatter(p.value, p.name) : p.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
}

// --- chart section wrapper ---
function ChartSection({ title, children, style }: { title: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ ...cardStyle, ...style }}>
      <span style={{ color: TEXT_DIM, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{title}</span>
      {children}
    </div>
  );
}

interface LeaderRow {
  key: string;
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  rounded?: boolean; // round avatar vs square thumbnail
  value: number;
  valueLabel: string;
}

function LeaderboardList({ rows, emptyText }: { rows: LeaderRow[]; emptyText: string }) {
  if (!rows.length) {
    return <div style={{ color: TEXT_DIM, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>{emptyText}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      {rows.map((r, i) => (
        <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: TEXT_DIM, fontSize: 13, fontWeight: 600, width: 18, textAlign: 'right' }}>{i + 1}</span>
          {r.imageUrl ? (
            <img
              src={r.imageUrl}
              alt=""
              style={{ width: 32, height: 32, borderRadius: r.rounded ? '50%' : 6, objectFit: 'cover', background: '#21262d', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: r.rounded ? '50%' : 6, background: '#21262d', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: TEXT, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
            {r.subtitle && <div style={{ color: TEXT_DIM, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subtitle}</div>}
          </div>
          <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {r.value.toLocaleString()} <span style={{ color: TEXT_DIM, fontWeight: 400, fontSize: 11 }}>{r.valueLabel}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- funnel chart (horizontal bars; success% vs step 1, drop% vs previous step) ---
interface FunnelStep { label: string; count: number; basic?: number; pro?: number; premium?: number }

function FunnelChart({ steps, accent }: { steps: FunnelStep[]; accent: string }) {
  // Bars scale to the largest step so a non-monotonic (non-cohort) funnel never
  // overflows; the % labels carry the true funnel semantics.
  const maxCount = Math.max(1, ...steps.map((s) => s.count));
  const first = steps[0]?.count ?? 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
      {steps.map((s, i) => {
        const widthPct = Math.max(2, (s.count / maxCount) * 100);
        const fromStart = first > 0 ? s.count / first : null; // success vs step 1
        const prev = i > 0 ? steps[i - 1].count : null;
        const dropRatio = prev != null && prev > 0 ? 1 - s.count / prev : null; // vs previous
        const hasTier = s.basic != null || s.pro != null || s.premium != null;
        return (
          <div key={s.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
              <span style={{ color: TEXT, fontSize: 13, fontWeight: 500 }}>
                <span style={{ color: TEXT_DIM, marginRight: 8 }}>{i + 1}</span>{s.label}
              </span>
              <span style={{ color: TEXT, fontSize: 15, fontWeight: 700 }}>{s.count.toLocaleString()}</span>
            </div>
            <div style={{ height: 24, background: '#21262d', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${widthPct}%`, height: '100%', background: accent, borderRadius: 6, transition: 'width .3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5, fontSize: 11, gap: 8 }}>
              <span style={{ color: TEXT_DIM }}>
                {hasTier && <>Basic {(s.basic ?? 0).toLocaleString()} · Pro {(s.pro ?? 0).toLocaleString()} · Premium {(s.premium ?? 0).toLocaleString()}</>}
              </span>
              <span style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                {fromStart != null && <span style={{ color: TEXT_DIM }}>{formatPct(fromStart)} of step 1</span>}
                {dropRatio != null && (
                  <span style={{ color: dropRatio > 0 ? RED : GREEN, fontWeight: 600 }}>
                    {dropRatio > 0 ? `▼ ${formatPct(dropRatio)} lower` : `▲ ${formatPct(-dropRatio)} higher`}
                  </span>
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const PLAN_COLORS: Record<string, string> = {
  free: '#8b949e',
  basic: BLUE,
  pro: GREEN,
  premium: PURPLE,
  unknown: '#484f58',
};

const VERSION_COLORS = [BLUE, PURPLE, GREEN, ORANGE, YELLOW, RED, '#79c0ff', '#d2a8ff', '#56d364', '#ffa657'];

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
  unknown: 'Unknown',
};

// Pro keeps the Pro plan's green so the tier name means one colour across both
// purchase charts; Lite has no plan counterpart and takes orange, which separates
// from green far better in a stack than a second warm tone would. `other` is the
// retired topup.1 and any unmapped SKU.
const TOPUP_COLORS: Record<'lite' | 'pro' | 'other', string> = {
  lite: ORANGE,
  pro: GREEN,
  other: '#484f58',
};

const TOPUP_LABELS: Record<'lite' | 'pro' | 'other', string> = {
  lite: 'Lite Pack',
  pro: 'Pro Pack',
  other: 'Other',
};

const PLATFORM_COLORS: Record<string, string> = {
  ios: BLUE,
  android: GREEN,
  unknown: '#484f58',
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  unknown: 'Unknown',
};

const FEATURE_COLORS: Record<string, string> = {
  rebuild: BLUE,
  blend: PURPLE,
  extension: GREEN,
  '(not set)': '#484f58',
};

const FEATURE_LABELS: Record<string, string> = {
  rebuild: 'Rebuild',
  blend: 'Blend',
  extension: 'Lens',
  '(not set)': 'Unspecified',
};

/** The original reporting workspace, retained at /dashboard/legacy. */
export default function LegacyOverviewPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [ga4, setGa4] = useState<GA4Data | null>(null);
  const [ga4Error, setGa4Error] = useState<string | null>(null);
  const [pub, setPub] = useState<PublicStats | null>(null);
  const [funnel, setFunnel] = useState<FunnelStats | null>(null);
  const [purchases, setPurchases] = useState<PurchaseStats | null>(null);
  const [leaderWindow, setLeaderWindow] = useState<'allTime' | 'last7Days'>('allTime');
  const [funnelWindow, setFunnelWindow] = useState<'all' | 'last30Days' | 'last7Days'>('last30Days');
  const [purchaseWindow, setPurchaseWindow] = useState<7 | 30>(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The crash breakdowns are their own GA4 reports, fetched by the modal when it
  // opens rather than on every dashboard load
  const [crashDetailOpen, setCrashDetailOpen] = useState(false);
  const [lensStatsOpen, setLensStatsOpen] = useState(false);
  const [userStatsOpen, setUserStatsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [dashRes, ga4Res, pubRes, funnelRes, purchaseRes] = await Promise.allSettled([
          apiRequest<DashboardStats & { success: boolean }>('/api/admin/dashboard-stats'),
          apiRequest<GA4Data & { success: boolean }>('/api/admin/ga4-stats?days=30'),
          apiRequest<PublicStats & { success: boolean }>('/api/stats'),
          apiRequest<FunnelStats & { success: boolean }>('/api/admin/funnel-stats'),
          apiRequest<PurchaseStats & { success: boolean }>('/api/admin/purchase-stats'),
        ]);

        if (dashRes.status === 'fulfilled' && dashRes.value.success !== false) {
          setStats(dashRes.value);
        } else {
          setError('Failed to load dashboard stats');
        }

        if (ga4Res.status === 'fulfilled' && ga4Res.value.success !== false) {
          setGa4(ga4Res.value);
        } else {
          const msg = ga4Res.status === 'rejected' ? (ga4Res.reason as Error).message : 'GA4 data unavailable';
          setGa4Error(msg);
        }

        if (pubRes.status === 'fulfilled' && pubRes.value.success !== false) {
          setPub(pubRes.value);
        }

        if (funnelRes.status === 'fulfilled' && funnelRes.value.success !== false) {
          setFunnel(funnelRes.value);
        }

        if (purchaseRes.status === 'fulfilled' && purchaseRes.value.success !== false) {
          setPurchases(purchaseRes.value);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: TEXT_DIM }}>Loading dashboard...</div>;
  }
  if (error || !stats) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: RED }}>{error || 'No data available'}</div>;
  }

  // Prepare chart data
  const genSeries = fillDailySeries(stats.dailyGenerations, 30);
  const userSeries = fillDailySeries(stats.dailyNewUsers, 30);
  const extSeries = fillDailySeries(stats.dailyNewExtensions, 30);

  // Plan pie data
  const pieData = Object.entries(stats.planBreakdown)
    .map(([plan, count]) => ({ name: PLAN_LABELS[plan] || plan, value: count, color: PLAN_COLORS[plan] || '#8b949e' }))
    .sort((a, b) => b.value - a.value);

  // Device platform pie data (iOS vs Android, by latest activity per user)
  const platformData = Object.entries(stats.platformDistribution || {})
    .map(([platform, count]) => ({ name: PLATFORM_LABELS[platform] || platform, value: count, color: PLATFORM_COLORS[platform] || '#8b949e' }))
    .sort((a, b) => b.value - a.value);
  const platformTotal = platformData.reduce((sum, d) => sum + d.value, 0);

  // App version distribution by user base (each user's last-seen app version).
  // Sourced from the DB, so each user counts once and totals sum to the user
  // base — unlike the GA4 active-user counts, which can exceed it.
  const appVersionData = Object.entries(stats.appVersionDistribution || {})
    .map(([version, count], i) => ({ name: version, value: count, color: VERSION_COLORS[i % VERSION_COLORS.length] }))
    .sort((a, b) => b.value - a.value);
  const appVersionTotal = appVersionData.reduce((sum, d) => sum + d.value, 0);

  // Leaderboards & referral (from the public /api/stats endpoint)
  const topExtensionRows: LeaderRow[] = (pub?.topExtensions?.[leaderWindow] || []).map((e) => ({
    key: e.extensionId,
    title: e.name,
    subtitle: `by ${e.creatorName}`,
    imageUrl: e.thumbnailImageUrl,
    rounded: false,
    value: e.generationCount,
    valueLabel: 'gens',
  }));
  const topCreatorRows: LeaderRow[] = (pub?.topCreators?.[leaderWindow] || []).map((c) => ({
    key: c.creatorId,
    title: c.name,
    imageUrl: c.avatarUrl,
    rounded: true,
    value: c.generationCount,
    valueLabel: 'gens',
  }));
  const referrerRows: LeaderRow[] = (pub?.topReferrers || []).map((r) => ({
    key: r.referrerId,
    title: r.name,
    imageUrl: r.avatarUrl,
    rounded: true,
    value: r.referralCount,
    valueLabel: 'referred',
  }));
  const referral = pub?.referralConversion;
  // Walk-in users: signed up without a referral code. They partition the non-internal
  // base together with referred users, so walk-in share = walkIn / (walkIn + referred).
  const walkInUsers = pub?.walkInUsers ?? 0;
  const acquisitionBase = walkInUsers + (referral?.totalReferred ?? 0);
  const walkInShare = acquisitionBase > 0 ? walkInUsers / acquisitionBase : null;
  // Users with too few credits to afford one generation (<8) who never bought a top-up.
  // Shown as a share of the non-internal user base (usersByPlan) — a friction signal.
  const exhaustedNoTopup = pub?.exhaustedNoTopupUsers ?? 0;
  const nonInternalBase = pub
    ? pub.usersByPlan.free + pub.usersByPlan.basic + (pub.usersByPlan.pro ?? 0) + pub.usersByPlan.premium
    : 0;
  const exhaustedShare = nonInternalBase > 0 ? exhaustedNoTopup / nonInternalBase : null;

  // Failed sections must never display compatibility defaults as measured values.
  const ga4Unavailable = (section: string) => ga4?.unavailable?.includes(section) ?? false;
  const retention = ga4?.retention;
  const toFeatureData = (entries: GA4FeatureEntry[] | undefined) => (entries || []).map((f) => ({
    name: FEATURE_LABELS[f.feature] || f.feature,
    value: f.count,
    color: FEATURE_COLORS[f.feature] || '#484f58',
  }));
  // "First action" feature usage is computed from the DB (generation `mode`) via
  // dashboard-stats, not GA4 — so it works retroactively with no custom-dimension
  // setup or app release.
  const firstGenData = toFeatureData(stats.firstGenerationBreakdown);
  const firstGenTotal = firstGenData.reduce((sum, d) => sum + d.value, 0);

  // Registered-user activity from the DB is the source of truth for DAU/WAU/MAU —
  // GA4's active users count anonymous app instances and overstate a 65-user base.
  const registered = stats.registeredActivity;
  // Activation: registered new users (last 30d) who completed their first generation.
  // Both numerator and denominator come straight from the DB (dashboard-stats), so it
  // works retroactively with no GA4 custom event or app release required.
  const activatedUsers = registered?.activatedNewUsers ?? 0;
  const activationRate = registered && registered.newUsers > 0 ? activatedUsers / registered.newUsers : null;
  // Avg generations per active user per week (uses registered WAU, the source of truth)
  const genPerActiveUser = registered && registered.wau > 0
    ? (stats.last7Days.generations / registered.wau)
    : null;

  // Funnels & behaviour cards for the selected window (client-side toggle over the
  // precomputed 3-window payload). Null until /api/admin/funnel-stats loads.
  const fw = funnel ? funnel[funnelWindow] : null;
  const af = fw?.activationFunnel;
  const mf = fw?.moneyFunnel;
  const notif = fw?.cards.notifications;
  const notifEnabledRate = notif && notif.total > 0 ? notif.enabled / notif.total : null;
  const activationSteps: FunnelStep[] = [
    { label: 'Start onboarding', count: af?.startOnboarding ?? 0 },
    { label: 'Finish onboarding', count: af?.finishOnboarding ?? 0 },
    { label: '1st FRAW (activated)', count: af?.firstFraw ?? 0 },
  ];
  const moneySteps: FunnelStep[] = [
    { label: '1st FRAW', count: mf?.firstFraw ?? 0 },
    { label: 'Paywall views', count: mf?.paywallViews.total ?? 0, basic: mf?.paywallViews.basic ?? 0, pro: mf?.paywallViews.pro ?? 0, premium: mf?.paywallViews.premium ?? 0 },
    { label: 'Paywall taps', count: mf?.paywallTaps.total ?? 0, basic: mf?.paywallTaps.basic ?? 0, pro: mf?.paywallTaps.pro ?? 0, premium: mf?.paywallTaps.premium ?? 0 },
    { label: 'Start checkout', count: mf?.startCheckout.total ?? 0, basic: mf?.startCheckout.basic ?? 0, pro: mf?.startCheckout.pro ?? 0, premium: mf?.startCheckout.premium ?? 0 },
    { label: 'Success purchase', count: mf?.successPurchase.total ?? 0, basic: mf?.successPurchase.basic ?? 0, pro: mf?.successPurchase.pro ?? 0, premium: mf?.successPurchase.premium ?? 0 },
  ];

  // Purchase trends: the toggle takes the tail of the 30-day window the endpoint
  // always returns, so switching windows re-renders rather than re-fetches.
  const subSeries = (purchases?.subscriptions ?? []).slice(-purchaseWindow);
  const topUpSeries = (purchases?.topUps ?? []).slice(-purchaseWindow);
  const sumKey = <T extends Record<string, any>>(rows: T[], key: keyof T) =>
    rows.reduce((sum, row) => sum + (row[key] as number), 0);
  const subTotals = {
    basic: sumKey(subSeries, 'basic'),
    pro: sumKey(subSeries, 'pro'),
    premium: sumKey(subSeries, 'premium'),
  };
  const subTotal = subTotals.basic + subTotals.pro + subTotals.premium;
  const topUpTotals = {
    lite: sumKey(topUpSeries, 'lite'),
    pro: sumKey(topUpSeries, 'pro'),
    other: sumKey(topUpSeries, 'other'),
  };
  const topUpTotal = topUpTotals.lite + topUpTotals.pro + topUpTotals.other;
  // Keeping an always-empty "Other" bar out of the legend, so the chart shows the
  // packs that actually sold rather than a permanent zero series.
  const topUpPacks = (['lite', 'pro', 'other'] as const).filter(
    (pack) => pack !== 'other' || topUpTotals.other > 0,
  );
  // Every day labelled at 7 days; every 7th at 30, matching the other daily bars.
  const purchaseTickInterval = purchaseWindow === 7 ? 0 : 6;

  const axisStyle = { fontSize: 11, fill: TEXT_DIM };
  const gridStroke = '#21262d';

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
        <div>
          <h2 style={{ color: TEXT, fontSize: 22, fontWeight: 600, margin: 0 }}>Legacy reports</h2>
          <p style={{ color: TEXT_DIM, fontSize: 12, margin: '7px 0 0' }}>Each report below keeps its own time range and definitions.</p>
        </div>
        <Link to="/dashboard" style={{ color: BLUE, fontSize: 12, whiteSpace: 'nowrap' }}>Back to period dashboard</Link>
      </div>

      {/* Alert cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon={<FiImage />} label="Total Images Generated" value={stats.totals.generations} color={BLUE} />
        <StatCard
          icon={<FiPackage />}
          label="Total Lenses"
          value={stats.totals.extensions}
          sub={`${stats.totals.publicExtensions.toLocaleString()} public`}
          color={PURPLE}
          onPress={() => setLensStatsOpen(true)}
        />
        <StatCard
          icon={<FiUsers />}
          label="Total Users"
          value={stats.totals.users}
          sub={`${stats.last7Days.newUsers.toLocaleString()} new in 7d`}
          color={GREEN}
          onPress={() => setUserStatsOpen(true)}
        />
        <StatCard
          icon={<FiCreditCard />}
          label="Out of Credits, No Top-up"
          value={pub ? exhaustedNoTopup : '—'}
          sub={pub
            ? `${exhaustedShare != null ? formatPct(exhaustedShare) + ' of users · ' : ''}<8 credits, never bought credits`
            : 'Stat unavailable'}
          color={RED}
        />
        <StatCard
          icon={<FiClock />}
          label="Pending Waitlist"
          value={stats.pendingWaitlist}
          color={YELLOW}
          onPress={() => navigate('/waitlist')}
        />
        <StatCard
          icon={<FiDollarSign />}
          label="Pending Payouts"
          value={stats.pendingPayouts}
          color={ORANGE}
          onPress={() => navigate('/payouts')}
        />
        <StatCard icon={<FiAlertTriangle />} label="Suspended Lenses" value={stats.suspendedExtensions} color={RED} />
        <StatCard
          icon={<FiBook />}
          label="Articles (7d)"
          value={stats.last7Days.articles}
          sub={`${stats.totals.articles.toLocaleString()} total`}
          color={YELLOW}
          onPress={() => navigate('/articles')}
        />
      </div>

      {/* Extension promotion performance */}
      {stats.extensionPromotion && (
        <ChartSection
          title={
            <span>
              <FiBell style={{ verticalAlign: -2, marginRight: 6 }} />
              Lens Promotion Performance — Last {stats.extensionPromotion.windowDays} Days
              {stats.extensionPromotion.campaigns > 0 && (
                <span style={{ color: TEXT_DIM, fontWeight: 400 }}>
                  {' '}({stats.extensionPromotion.campaigns.toLocaleString()} campaigns · {stats.extensionPromotion.recipients.toLocaleString()} recipients)
                </span>
              )}
            </span>
          }
          style={{ marginBottom: 20 }}
        >
          {stats.extensionPromotion.recipients === 0 ? (
            <div style={{ color: TEXT_DIM, fontSize: 14, padding: '8px 0' }}>
              No lens-promotion campaigns in the last {stats.extensionPromotion.windowDays} days.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                <StatCard
                  icon={<FiMousePointer />}
                  label="Interaction Rate"
                  value={formatPct(stats.extensionPromotion.interactionRate)}
                  sub={`${stats.extensionPromotion.interactedCount.toLocaleString()} tapped of ${stats.extensionPromotion.recipients.toLocaleString()}`}
                  color={PURPLE}
                />
                <StatCard
                  icon={<FiZap />}
                  label="Usage Rate (24h after read)"
                  value={formatPct(stats.extensionPromotion.usageRate)}
                  sub={`${stats.extensionPromotion.usedCount.toLocaleString()} used the lens${stats.extensionPromotion.approx ? ' (approx)' : ''}`}
                  color={GREEN}
                />
                <StatCard
                  icon={<FiEye />}
                  label="Read Rate"
                  value={formatPct(stats.extensionPromotion.readRate)}
                  sub={`${stats.extensionPromotion.readCount.toLocaleString()} read of ${stats.extensionPromotion.recipients.toLocaleString()}`}
                  color={BLUE}
                />
                <StatCard
                  icon={<FiTrendingUp />}
                  label="Tap → Use Conversion"
                  value={formatPct(stats.extensionPromotion.usageOfInteractedRate)}
                  sub="of users who tapped went on to use the lens"
                  color={ORANGE}
                />
              </div>
              <div style={{ color: TEXT_DIM, fontSize: 12, marginTop: 12 }}>
                Interaction = tapped the notification (in-app or from a push). Usage = used the promoted
                lens within 24h after reading. Rates are over all recipients.
                {stats.extensionPromotion.approx && ' “approx” = some campaigns predate read-time tracking, so their usage is anchored to send time.'}
              </div>
            </>
          )}
        </ChartSection>
      )}

      {/* Generations area chart */}
      <ChartSection title={`Image Generations — Last 30 Days (${stats.last30Days.generations.toLocaleString()} total · ${stats.last7Days.generations.toLocaleString()} in 7d)`} style={{ marginBottom: 20 }}>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={genSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="genGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={4} />
            <YAxis tick={axisStyle} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="count" name="Generations" stroke={BLUE} fill="url(#genGrad)" strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartSection>

      {/* Two-column: New Users bar + Extensions bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
        <ChartSection title={`New Users — Last 30 Days (${stats.last30Days.newUsers.toLocaleString()} total · ${stats.totals.users.toLocaleString()} all time)`}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={userSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={6} />
              <YAxis tick={axisStyle} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" name="New Users" fill={GREEN} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>

        <ChartSection title={`Lenses Created — Last 30 Days (${stats.last30Days.extensions.toLocaleString()} total · ${stats.totals.extensions.toLocaleString()} all time)`}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={extSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={6} />
              <YAxis tick={axisStyle} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" name="Lenses" fill={PURPLE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
      </div>

      {/* Two-column: User Plans pie + Device Platform pie */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
        <ChartSection title="User Plans">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
                nameKey="name"
                stroke="none"
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT }}
                itemStyle={{ color: TEXT }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartSection>

        <ChartSection title={`Device Platform — iOS vs Android (${platformTotal.toLocaleString()} users)`}>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                  stroke="none"
                >
                  {platformData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT }}
                  itemStyle={{ color: TEXT }}
                  formatter={(value: any, name: any) => {
                    const pct = platformTotal > 0 ? ((Number(value) / platformTotal) * 100).toFixed(1) : '0';
                    return [`${Number(value).toLocaleString()} users (${pct}%)`, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280, color: TEXT_DIM, fontSize: 13 }}>
              No device platform data yet
            </div>
          )}
        </ChartSection>
      </div>

      {/* Users by App Version (DB — each user's last-seen version, one per user) */}
      <ChartSection
        title={`Users by App Version (${appVersionTotal.toLocaleString()} users · latest version per user)`}
        style={{ marginBottom: 20 }}
      >
        {appVersionData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={appVersionData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                stroke="none"
              >
                {appVersionData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT }}
                itemStyle={{ color: TEXT }}
                formatter={(value: any, name: any) => {
                  const pct = appVersionTotal > 0 ? ((Number(value) / appVersionTotal) * 100).toFixed(1) : '0';
                  return [`${Number(value).toLocaleString()} users (${pct}%)`, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280, color: TEXT_DIM, fontSize: 13 }}>
            No app version data yet
          </div>
        )}
      </ChartSection>

      {/* Purchases — completed subscription and top-up purchases per day */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 8 }}>
        <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, margin: 0 }}>Purchases</h3>
        <div style={{ display: 'flex', gap: 4, background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 3 }}>
          {([7, 30] as const).map((w) => (
            <button
              key={w}
              onClick={() => setPurchaseWindow(w)}
              style={{
                padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: purchaseWindow === w ? BLUE : 'transparent',
                color: purchaseWindow === w ? '#fff' : TEXT_DIM,
              }}
            >
              {`Last ${w} days`}
            </button>
          ))}
        </div>
      </div>

      {purchases ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 12 }}>
            <ChartSection
              title={`Subscriptions — Last ${purchaseWindow} Days (${subTotal.toLocaleString()} total · Basic ${subTotals.basic.toLocaleString()} · Pro ${subTotals.pro.toLocaleString()} · Premium ${subTotals.premium.toLocaleString()})`}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={subSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={purchaseTickInterval} />
                  <YAxis tick={axisStyle} allowDecimals={false} />
                  <Tooltip content={<StackedTooltip />} cursor={{ fill: '#ffffff0d' }} />
                  {/* itemSorter={null} keeps the legend in the order the Bars are declared.
                      Recharts defaults it to 'value', which sorts the entries alphabetically
                      by label — that renders Basic, Premium, Pro, splitting the tiers instead
                      of climbing them. The Tooltip needs no such prop: its sorter lives in
                      DefaultTooltipContent, which a custom `content` bypasses. */}
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
                  {/* One stackId, so the three plans add up to the day's subscription count,
                      stacking bottom-up in this order so Premium sits on top. Square tops,
                      unlike the single-series bars above: rounding the last series rounds an
                      invisible segment on any day that plan sold nothing. */}
                  {(['basic', 'pro', 'premium'] as const).map((plan) => (
                    <Bar key={plan} dataKey={plan} stackId="subs" name={PLAN_LABELS[plan]} fill={PLAN_COLORS[plan]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartSection>

            <ChartSection
              title={`Top-ups — Last ${purchaseWindow} Days (${topUpTotal.toLocaleString()} total · Lite ${topUpTotals.lite.toLocaleString()} · Pro ${topUpTotals.pro.toLocaleString()})`}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topUpSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={purchaseTickInterval} />
                  <YAxis tick={axisStyle} allowDecimals={false} />
                  <Tooltip content={<StackedTooltip />} cursor={{ fill: '#ffffff0d' }} />
                  {/* As above — the default 'value' sorter would read Lite Pack, Other, Pro Pack. */}
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
                  {topUpPacks.map((pack) => (
                    <Bar key={pack} dataKey={pack} stackId="topups" name={TOPUP_LABELS[pack]} fill={TOPUP_COLORS[pack]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartSection>
          </div>
          <div style={{ color: TEXT_DIM, fontSize: 12, marginBottom: 20 }}>
            Completed purchases, counted on the day payment settled — internal/test accounts and
            sandbox purchases included. Web (Stripe) writes a row per paid invoice, so a web renewal
            counts again; a mobile renewal does not, so these are closer to new/changed subscriptions
            on mobile and to payments on web. The Lite and Pro bars merge each pack's mobile and web
            SKUs, which grant different credit amounts.
          </div>
        </>
      ) : (
        <div style={{ ...cardStyle, color: TEXT_DIM, fontSize: 13, textAlign: 'center', padding: '40px 24px', marginBottom: 20 }}>
          Purchase stats unavailable
        </div>
      )}

      {/* Leaderboards & Referral (public stats) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 8 }}>
        <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, margin: 0 }}>Top Performers & Referrals</h3>
        <div style={{ display: 'flex', gap: 4, background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 3 }}>
          {(['allTime', 'last7Days'] as const).map((w) => (
            <button
              key={w}
              onClick={() => setLeaderWindow(w)}
              style={{
                padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: leaderWindow === w ? BLUE : 'transparent',
                color: leaderWindow === w ? '#fff' : TEXT_DIM,
              }}
            >
              {w === 'allTime' ? 'All time' : 'Last 7 days'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
        <ChartSection title={<span><FiAward style={{ verticalAlign: -2, marginRight: 6 }} />Top Lenses</span>}>
          <LeaderboardList rows={topExtensionRows} emptyText={pub ? 'No lens usage yet' : 'Leaderboard unavailable'} />
        </ChartSection>
        <ChartSection title={<span><FiStar style={{ verticalAlign: -2, marginRight: 6 }} />Top Creators</span>}>
          <LeaderboardList rows={topCreatorRows} emptyText={pub ? 'No creator activity yet' : 'Leaderboard unavailable'} />
        </ChartSection>
      </div>

      {/* Referral conversion + K-factor + top referrers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 28 }}>
        <ChartSection title={<span><FiGift style={{ verticalAlign: -2, marginRight: 6 }} />Referral Conversion</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
            <div>
              <div style={{ color: TEXT, fontSize: 26, fontWeight: 700 }}>{formatPct(referral?.conversionRate)}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>Paid conversion of referred users</div>
            </div>
            <div>
              <div style={{ color: TEXT, fontSize: 26, fontWeight: 700 }}>{referral?.kFactor != null ? referral.kFactor.toFixed(3) : '—'}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>K-factor (viral coefficient)</div>
            </div>
            <div>
              <div style={{ color: TEXT, fontSize: 20, fontWeight: 700 }}>{(referral?.totalReferred ?? 0).toLocaleString()}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>Total referred</div>
            </div>
            <div>
              <div style={{ color: TEXT, fontSize: 20, fontWeight: 700 }}>{(referral?.paidReferred ?? 0).toLocaleString()}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>Referred → paid</div>
            </div>
            <div>
              <div style={{ color: TEXT, fontSize: 20, fontWeight: 700 }}>{walkInUsers.toLocaleString()}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>Walk-in users (no referral)</div>
            </div>
            <div>
              <div style={{ color: TEXT, fontSize: 20, fontWeight: 700 }}>{formatPct(walkInShare)}</div>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>Walk-in share of signups</div>
            </div>
          </div>
          <div style={{ color: TEXT_DIM, fontSize: 11, marginTop: 12 }}>
            Internal/test accounts excluded. Walk-in = registered without a referral code.
          </div>
        </ChartSection>
        <ChartSection title={<span><FiUsers style={{ verticalAlign: -2, marginRight: 6 }} />Top Referrers</span>}>
          <LeaderboardList rows={referrerRows} emptyText={pub ? 'No referrals yet' : 'Leaderboard unavailable'} />
        </ChartSection>
      </div>

      {/* Registered-user activity (DB — source of truth, excludes anonymous instances) */}
      <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, marginBottom: 4, marginTop: 8 }}>Active Users — Registered</h3>
      <p style={{ color: TEXT_DIM, fontSize: 12, margin: '0 0 16px' }}>
        From the database (each user's last authenticated app launch), so anonymous / signed-out instances are excluded — unlike the GA4 active-user counts below.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon={<FiUsers />} label="DAU (active 1-day)" value={registered?.dau ?? 0} color={BLUE} />
        <StatCard icon={<FiUsers />} label="WAU (active 7-day)" value={registered?.wau ?? 0} color={GREEN} />
        <StatCard icon={<FiUsers />} label="MAU (active 28-day)" value={registered?.mau ?? 0} color={PURPLE} />
        <StatCard icon={<FiTrendingUp />} label="Stickiness (DAU/WAU)" value={formatPct(registered?.stickiness)} color={ORANGE} />
        <StatCard
          icon={<FiZap />}
          label="Activation %"
          value={activationRate != null ? formatPct(activationRate) : '—'}
          sub={registered ? `${activatedUsers.toLocaleString()} of ${registered.newUsers.toLocaleString()} new users generated` : 'Awaiting data'}
          color={YELLOW}
        />
        <StatCard
          icon={<FiImage />}
          label="Generations / Active User (7d)"
          value={genPerActiveUser != null ? genPerActiveUser.toFixed(1) : '—'}
          sub="Last 7d generations ÷ registered WAU"
          color={GREEN}
        />
      </div>

      {/* Behaviour & Funnels — paywall/onboarding/permission events come from the app's
          AnalyticsEvent store (zero until the instrumented app ships); firstFRAW/checkout/
          purchase are derived from existing data. Window toggle is client-side. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginTop: 8 }}>
        <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, margin: 0 }}>Behaviour & Funnels</h3>
        <div style={{ display: 'flex', gap: 4, background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 3 }}>
          {(['all', 'last30Days', 'last7Days'] as const).map((w) => (
            <button
              key={w}
              onClick={() => setFunnelWindow(w)}
              style={{
                padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: funnelWindow === w ? BLUE : 'transparent',
                color: funnelWindow === w ? '#fff' : TEXT_DIM,
              }}
            >
              {w === 'all' ? 'Retained history' : w === 'last30Days' ? 'Last 30 days' : 'Last 7 days'}
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: TEXT_DIM, fontSize: 12, margin: '0 0 16px' }}>
        Existing registered users only; internal, deleted and missing accounts are excluded. Counts are independent distinct users per step within the window, not an ordered signup cohort. A user may appear under multiple tiers or permission results. Ratios compare counts, not conversion or dropout. App events retain up to 2 years; app-launch/login ActivityLogs no longer expire automatically, but history deleted under the former 90-day policy cannot be restored. Generation and purchase records have separate history. Notification enabled is the current snapshot; granted/denied are window events.
      </p>

      {/* Behaviour cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard
          icon={<FiCreditCard />}
          label="Paywall Views"
          value={mf?.paywallViews.total ?? 0}
          sub={mf ? `Basic ${mf.paywallViews.basic.toLocaleString()} · Pro ${(mf.paywallViews.pro ?? 0).toLocaleString()} · Premium ${mf.paywallViews.premium.toLocaleString()}` : 'Awaiting app data'}
          color={BLUE}
        />
        <StatCard
          icon={<FiZap />}
          label="Paywall Taps"
          value={mf?.paywallTaps.total ?? 0}
          sub={mf ? `Basic ${mf.paywallTaps.basic.toLocaleString()} · Pro ${(mf.paywallTaps.pro ?? 0).toLocaleString()} · Premium ${mf.paywallTaps.premium.toLocaleString()}` : 'Awaiting app data'}
          color={PURPLE}
        />
        <StatCard
          icon={<FiBell />}
          label="Notifications Enabled"
          value={notifEnabledRate != null ? formatPct(notifEnabledRate) : '—'}
          sub={notif ? `${notif.enabled.toLocaleString()} of ${notif.total.toLocaleString()} users · ${notif.granted.toLocaleString()} granted / ${notif.denied.toLocaleString()} denied` : 'Awaiting data'}
          color={GREEN}
        />
      </div>

      {/* Activation & Money funnels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 28 }}>
        <ChartSection title={<span><FiActivity style={{ verticalAlign: -2, marginRight: 6 }} />Activation Funnel</span>}>
          {funnel
            ? <FunnelChart steps={activationSteps} accent={BLUE} />
            : <div style={{ color: TEXT_DIM, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Funnel data unavailable</div>}
        </ChartSection>
        <ChartSection title={<span><FiDollarSign style={{ verticalAlign: -2, marginRight: 6 }} />Money Funnel</span>}>
          {funnel
            ? <FunnelChart steps={moneySteps} accent={GREEN} />
            : <div style={{ color: TEXT_DIM, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Funnel data unavailable</div>}
        </ChartSection>
      </div>

      {/* Generation feature usage — the "first action" each user takes, computed from the
          DB (generation `mode`), so it's always available (no GA4 setup / app release) and retroactive. */}
      <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, marginBottom: 16, marginTop: 8 }}>Generation Feature Usage</h3>
      <ChartSection title={<span><FiZap style={{ verticalAlign: -2, marginRight: 6 }} />First Generation by Feature (all-time)</span>} style={{ marginBottom: 20 }}>
        <p style={{ color: TEXT_DIM, fontSize: 12, margin: '0 0 8px' }}>
          The first action each user takes — the feature of their first completed generation (Rebuild / Blend / Lens), counted as distinct users across all time.
        </p>
        {firstGenTotal > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={firstGenData} cx="50%" cy="50%" innerRadius={65} outerRadius={110} paddingAngle={3} dataKey="value" nameKey="name" stroke="none">
                {firstGenData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT }}
                itemStyle={{ color: TEXT }}
                formatter={(value: any, name: any) => {
                  const pct = firstGenTotal > 0 ? ((Number(value) / firstGenTotal) * 100).toFixed(1) : '0';
                  return [`${Number(value).toLocaleString()} users (${pct}%)`, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280, color: TEXT_DIM, fontSize: 13, textAlign: 'center', padding: '0 20px' }}>
            No completed generations yet.
          </div>
        )}
      </ChartSection>

      {/* GA4 App Analytics */}
      <h3 style={{ color: TEXT, fontSize: 18, fontWeight: 600, marginBottom: 16, marginTop: 8 }}>App Analytics (Firebase / GA4)</h3>
      {ga4Error ? (
        <div style={{ ...cardStyle, color: TEXT_DIM, fontSize: 13, marginBottom: 20 }}>
          GA4 analytics unavailable: {ga4Error}
        </div>
      ) : ga4 ? (
        <>
          <p style={{ color: TEXT_DIM, fontSize: 12 }}>Android/iOS only. Sessions are GA visits, distinct from Backend app launches. Relative date windows include today.</p>
          {ga4.unavailable?.length ? <p role="status" style={{ color: TEXT_DIM }}>Some GA4 reports are unavailable: {ga4.unavailable.join(', ')}. Failed sections are not measured zeros.</p> : null}
          {/* GA4-native engagement cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
            <StatCard
              icon={<FiActivity />}
              label="Avg Engagement / Session (30d)"
              value={ga4Unavailable('engagement') || ga4.avgEngagementPerSession == null ? 'Unavailable' : formatDuration(ga4.avgEngagementPerSession)}
              sub="Foreground engagement ÷ sessions"
              color={BLUE}
            />
            <StatCard
              icon={<FiSmartphone />}
              label="App Sessions (30d)"
              value={ga4Unavailable('summary') ? 'Unavailable' : ga4.summary.sessions}
              sub={ga4Unavailable('summary') ? 'GA4 summary unavailable' : `${ga4.summary.activeUsers.toLocaleString()} GA4 active (incl. signed-out) · ${ga4.summary.screenPageViews.toLocaleString()} screen views`}
              color={GREEN}
            />
            <StatCard
              icon={<FiUsers />}
              label="GA4 Active (incl. anonymous)"
              value={ga4Unavailable('activeUsers') || !ga4.activeUsers ? 'Unavailable' : ga4.activeUsers.wau}
              sub="7-day — counts signed-out instances; see registered WAU above"
              color="#8b949e"
            />
            <StatCard
              icon={<FiAlertTriangle />}
              label="Crash-Free Users (30d)"
              value={ga4Unavailable('crashFree') ? 'Unavailable' : ga4.crashFree?.crashFreeUsersRate != null ? formatPct(ga4.crashFree.crashFreeUsersRate) : '—'}
              sub={ga4Unavailable('crashFree') ? 'Crash report or active-user denominator unavailable' : ga4.crashFree && ga4.crashFree.activeUsers > 0
                ? `${ga4.crashFree.crashes.toLocaleString()} crashes · ${ga4.crashFree.crashingUsers.toLocaleString()} users affected`
                : 'Awaiting Crashlytics in a released build'}
              color={GREEN}
              onPress={() => setCrashDetailOpen(true)}
            />
          </div>

          {/* Retention (GA4 cohort) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
            <ChartSection title={<span><FiRepeat style={{ verticalAlign: -2, marginRight: 6 }} />Retention (blended cohort)</span>}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 12 }}>
                {([['D1', retention?.d1], ['D7', retention?.d7], ['D30', retention?.d30]] as const).map(([label, val]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ color: TEXT, fontSize: 26, fontWeight: 700 }}>{ga4Unavailable('retention') || !retention || ga4.retentionStatus === 'unavailable' ? 'Unavailable' : ga4.retentionStatus === 'empty' ? formatPct(0) : val == null ? 'Not enough data' : formatPct(val)}</div>
                    <div style={{ color: TEXT_DIM, fontSize: 12 }}>{label} retention</div>
                  </div>
                ))}
              </div>
              <div style={{ color: TEXT_DIM, fontSize: 11, marginTop: 16 }}>
                Share of new users returning N days after first open (blended 30-day acquisition cohort).
                {ga4.retentionStatus === 'empty' && !ga4Unavailable('retention') ? ' GA4 connected successfully; no cohort users were returned, so these cards display 0%.' : ''}
              </div>
            </ChartSection>

          </div>

          {/* App Version Distribution (past 7 days) */}
          {ga4Unavailable('appVersions') ? <p style={{ color: TEXT_DIM }}>App versions unavailable</p> : ga4.appVersions.length > 0 ? (
            <ChartSection title="Active App Versions — Last 7 Days (GA4)" style={{ marginBottom: 20 }}>
              <p style={{ color: TEXT_DIM, fontSize: 12, margin: '0 0 8px' }}>
                GA4 active users per version. A user who upgraded is counted under each version they
                used, so these counts are not additive and can exceed the total user base. For a
                one-per-user breakdown, see “Users by App Version” above.
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={ga4.appVersions.map((v, i) => ({
                      name: v.version,
                      value: v.activeUsers,
                      color: VERSION_COLORS[i % VERSION_COLORS.length],
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    stroke="none"
                  >
                    {ga4.appVersions.map((_, i) => (
                      <Cell key={i} fill={VERSION_COLORS[i % VERSION_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }: any) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0];
                      // recharts v3 doesn't surface the Pie sector name at payload[].name —
                      // it only lives on the original datum (payload[].payload.name).
                      const version = p?.payload?.name ?? p?.name ?? '';
                      return (
                        <div style={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT, padding: '6px 10px' }}>
                          {version}: {Number(p?.value).toLocaleString()} users
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: TEXT_DIM }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartSection>
          ) : null}

          <GA4EventReports data={ga4} />
          {/* GA4 charts: session duration + sessions */}
          {ga4Unavailable('daily') ? <p style={{ color: TEXT_DIM }}>Daily App sessions and session duration unavailable</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
            <ChartSection title="Raw Session Duration (seconds) — Last 30 Days (GA4; often inflated — prefer engagement time above)">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={ga4.daily} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={6} />
                  <YAxis tick={axisStyle} />
                  <Tooltip content={<GA4Tooltip formatter={(v: number, name: string) => name === 'Avg Duration' ? formatDuration(v) : v.toLocaleString()} />} />
                  <Line type="monotone" dataKey="averageSessionDuration" name="Avg Duration" stroke={ORANGE} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <ChartSection title="App Sessions — Last 30 Days">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={ga4.daily} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisStyle} interval={6} />
                  <YAxis tick={axisStyle} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="sessions" name="Sessions" fill={GREEN} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartSection>
          </div>
          )}
        </>
      ) : null}

      <CrashDetailModal
        isOpen={crashDetailOpen}
        onClose={() => setCrashDetailOpen(false)}
        days={30}
      />

      <LensStatsModal
        isOpen={lensStatsOpen}
        onClose={() => setLensStatsOpen(false)}
        onViewAll={() => navigate('/extensions')}
      />

      <UserStatsModal
        isOpen={userStatsOpen}
        onClose={() => setUserStatsOpen(false)}
        onViewAll={() => navigate('/users')}
      />
    </div>
  );
}
